import React, { useEffect, useMemo, useState } from "react";
import { Card, Input, Layout, Typography } from "antd";
import { displayMessage, getAPI } from "../../utils/common";
import { KITCHEN_ISSUE_INVENTORY } from "../../constants/api";
import { ERROR_MSG_TYPE } from "../../constants/messages";
import LayoutFooter from "../inc/LayoutFooter";
import LayoutHeader from "../inc/LayoutHeader";

const { Content } = Layout;
const { Title } = Typography;

function KitchenIssueInventory() {
  const [inventoryData, setInventoryData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");

  useEffect(() => {
    fetchInventory();
  }, []);

  const fetchInventory = () => {
    setLoading(true);
    getAPI(
      KITCHEN_ISSUE_INVENTORY,
      (res) => {
        if (res.status) {
          setInventoryData(res.data);
        } else {
          displayMessage(ERROR_MSG_TYPE, res.message);
          console.error(res.message);
        }
        setLoading(false);
      },
      () => setLoading(false),
    );
  };

  const filteredInventory = useMemo(() => {
    return inventoryData.filter((item) =>
      `${item.item_name} ${item.item_code}`
        .toLowerCase()
        .includes(searchText.toLowerCase()),
    );
  }, [inventoryData, searchText]);

  const getStatus = (qty) => {
    if (qty < 5)
      return {
        label: "Critical",
        dot: "#A32D2D",
        tag: { bg: "#FCEBEB", color: "#A32D2D" },
      };

    if (qty < 20)
      return {
        label: "Low",
        dot: "#BA7517",
        tag: { bg: "#FAEEDA", color: "#854F0B" },
      };

    return { label: null, dot: "#3B6D11", tag: null };
  };

  const totalItems = filteredInventory.length;

  const lowCount = filteredInventory.filter(
    (i) => i.current_inventory >= 5 && i.current_inventory < 20,
  ).length;

  const criticalCount = filteredInventory.filter(
    (i) => i.current_inventory < 5,
  ).length;

  const styles = {
    statBar: {
      display: "grid",
      gridTemplateColumns: "repeat(3, 1fr)",
      gap: "12px",
      marginBottom: "1.25rem",
    },
    statBox: {
      background: "#fff",
      borderRadius: "10px",
      padding: "14px 16px",
      border: "0.5px solid #e0e0e0",
    },
    statLabel: {
      fontSize: "12px",
      color: "#888",
      margin: "0 0 4px",
    },
    legend: {
      display: "flex",
      gap: "16px",
      marginBottom: "1.25rem",
      flexWrap: "wrap",
    },
    legendItem: {
      fontSize: "12px",
      color: "#888",
      display: "flex",
      alignItems: "center",
      gap: "6px",
    },
    sectionTitle: {
      fontSize: "12px",
      fontWeight: 500,
      color: "#aaa",
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      marginBottom: "12px",
    },
    grid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
      gap: "12px",
    },
    card: {
      background: "#fff",
      borderRadius: "12px",
      border: "0.5px solid #e8e8e8",
      padding: "14px 16px",
      position: "relative",
      transition: "border-color 0.15s",
      cursor: "default",
      minHeight: "130px",
    },
    itemName: {
      fontSize: "14px",
      fontWeight: 500,
      margin: "0 0 3px",
      color: "#1a1a1a",
      paddingRight: "16px",
    },
    itemCode: {
      fontSize: "12px",
      color: "#aaa",
      margin: "0 0 12px",
    },
    qty: {
      fontSize: "26px",
      fontWeight: 500,
      color: "#1a1a1a",
      margin: 0,
    },
    qtyLabel: {
      fontSize: "11px",
      color: "#bbb",
      margin: "2px 0 0",
    },
  };

  if (loading) {
    return (
      <Layout>
        <LayoutHeader />

        <Content style={styles.content}>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "60vh",
            }}
          >
            <div style={{ textAlign: "center" }}>
              <div
                style={{
                  width: "32px",
                  height: "32px",
                  border: "2px solid #e0e0e0",
                  borderTop: "2px solid #555",
                  borderRadius: "50%",
                  animation: "spin 0.8s linear infinite",
                  margin: "0 auto 12px",
                }}
              />

              <p style={{ color: "#aaa", fontSize: "13px", margin: 0 }}>
                Loading inventory...
              </p>

              <style>{`
                @keyframes spin {
                  to {
                    transform: rotate(360deg);
                  }
                }
              `}</style>
            </div>
          </div>
        </Content>

        <LayoutFooter />
      </Layout>
    );
  }

  return (
    <Layout>
      <LayoutHeader />

      <Content>
        <Card
          title="Kitchen Issue Inventory"
          extra={
            <>
              {/* Search */}
              <div style={{ marginBottom: 16 }}>
                <Input.Search
                  placeholder="Search item name or code"
                  allowClear
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  enterButton
                  style={{ maxWidth: 400 }}
                />
              </div>
            </>
          }
        >
          {/* Stat summary */}
          <div style={styles.statBar}>
            <div style={styles.statBox}>
              <p style={styles.statLabel}>Total items</p>
              <p style={{ fontSize: "22px", fontWeight: 500, margin: 0 }}>
                {totalItems}
              </p>
            </div>

            <div style={styles.statBox}>
              <p style={styles.statLabel}>Low stock</p>
              <p
                style={{
                  fontSize: "22px",
                  fontWeight: 500,
                  margin: 0,
                  color: "#BA7517",
                }}
              >
                {lowCount}
              </p>
            </div>

            <div style={styles.statBox}>
              <p style={styles.statLabel}>Critical</p>
              <p
                style={{
                  fontSize: "22px",
                  fontWeight: 500,
                  margin: 0,
                  color: "#A32D2D",
                }}
              >
                {criticalCount}
              </p>
            </div>
          </div>

          {/* Legend */}
          <div style={styles.legend}>
            {[
              { color: "#3B6D11", label: "In stock" },
              { color: "#BA7517", label: "Low stock (<20)" },
              { color: "#A32D2D", label: "Critical (<5)" },
            ].map(({ color, label }) => (
              <span key={label} style={styles.legendItem}>
                <span
                  style={{
                    width: 7,
                    height: 7,
                    borderRadius: "50%",
                    background: color,
                    display: "inline-block",
                  }}
                />
                {label}
              </span>
            ))}
          </div>

          {/* Section label */}
          <p style={styles.sectionTitle}>All items</p>

          {/* Inventory grid */}
          <div style={styles.grid}>
            {filteredInventory.length > 0 ? (
              filteredInventory.map((item) => {
                const status = getStatus(item.current_inventory);

                return (
                  <div key={item.item_id} style={styles.card}>
                    {/* Status dot */}
                    <span
                      style={{
                        width: 7,
                        height: 7,
                        borderRadius: "50%",
                        background: status.dot,
                        position: "absolute",
                        top: 14,
                        right: 14,
                      }}
                    />

                    <p style={styles.itemName}>{item.item_name}</p>

                    <p style={styles.itemCode}>{item.item_code}</p>

                    <p style={styles.qty}>
                      {item.current_inventory} {item.unit}
                    </p>

                    <p style={styles.qtyLabel}>units in stock</p>

                    {/* Low / Critical tag */}
                    {status.tag && (
                      <span
                        style={{
                          position: "absolute",
                          bottom: 12,
                          right: 12,
                          fontSize: "10px",
                          fontWeight: 500,
                          padding: "2px 7px",
                          borderRadius: "4px",
                          background: status.tag.bg,
                          color: status.tag.color,
                        }}
                      >
                        {status.label}
                      </span>
                    )}
                  </div>
                );
              })
            ) : (
              <div
                style={{
                  gridColumn: "1 / -1",
                  textAlign: "center",
                  padding: "40px 0",
                  color: "#999",
                }}
              >
                No items found
              </div>
            )}
          </div>
        </Card>
      </Content>

      <LayoutFooter />
    </Layout>
  );
}

export default KitchenIssueInventory;
